import { default as React } from "react";
import {
    CheckboxFilters,
    MoreFilters,
} from "web/react/components/feeds-filters/feeds-filter-more-filters";
import { FilterManager } from "web/react/components/feeds-filters/hooks/use-filter-manager";
import { useFilterContext } from "web/react/components/filters-emo/filter-container-emo";
import { FILTER_SECTION } from "web/react/components/filters-emo/filter-utils";
import LoadingSpinner from "web/react/components/loading-spinner/loading-spinner";
import {
    CategoryFilterOption,
    ColorsFilterSection,
    FilteredScrollAreaFilterOptionGroup,
    InputFilterOption,
    NormalFilterSection,
    PairOfSelectsFilterSection,
    SizesFilterSection,
    UniversalFilterSection,
} from "web/types/universal-filters";
import styles from "./filter-content-container.module.css";

const FilterOptionGroup = React.lazy(() =>
    // Handling named exports with React.lazy
    import("web/react/components/filter-option-group/filter-option-group").then((module) => ({
        default: module.FilterOptionGroup,
    }))
);

const PriceFilter = React.lazy(() =>
    // Handling named exports with React.lazy
    import("web/react/components/feeds-filters/feed-filter-prices/feed-filter-prices").then(
        (module) => ({ default: module.PriceFilter })
    )
);

const FeedFilterSizes = React.lazy(() =>
    // Handling named exports with React.lazy
    import("web/react/components/feeds-filters/feed-filter-sizes/feed-filter-sizes").then(
        (module) => ({ default: module.FeedFilterSizes })
    )
);

const FeedFilterColors = React.lazy(() =>
    // Handling named exports with React.lazy
    import("web/react/components/feeds-filters/feed-filter-colors/feed-filter-colors").then(
        (module) => ({ default: module.FeedFilterColors })
    )
);

const FeedFilterGeneric = React.lazy(() =>
    // Handling named exports with React.lazy
    import("web/react/components/feeds-filters/feed-filter-generic/feed-filter-generic").then(
        (module) => ({ default: module.FeedFilterGeneric })
    )
);

const Categories = React.lazy(() =>
    // Handling named exports with React.lazy
    import("web/react/components/feeds-filters/feed-filter-categories/feed-filter-categories").then(
        (module) => ({ default: module.Categories })
    )
);

const AttributeFilters = React.lazy(() =>
    import("web/react/components/feeds-filters/feed-filter-attributes/feed-filter-attributes").then(
        (module) => ({ default: module.AttributeFilters })
    )
);

const SpinnerEle = (): JSX.Element => (
    <div className={styles.spinner}>
        <LoadingSpinner />
    </div>
);

interface FilterContentContainerProps
    extends Pick<FilterManager, "lazyFacetFetch" | "lazyFacetIsLoading" | "clearSelectedFilters"> {
    activeFilterId: string;
    navigateToTopFilter?: (filterId: string) => void;
    onFilterPillClick?: (filterData: UniversalFilterSection) => void;
    onClearAllClick: () => void;
}

function isAnyCategoryOptionChecked(section: UniversalFilterSection | undefined): boolean {
    // Check if any category option is checked & at least one child is checked
    const clothingOption = section?.option_groups[0].options?.[0];
    return !!(clothingOption?.checked && clothingOption.children?.some((child) => child.checked));
}

export function FilterContentContainer({
    activeFilterId,
    ...props
}: FilterContentContainerProps): JSX.Element | null {
    const { displayedFilters, selectedFilterInfo, pendingFilterInfo, onFilterItemClick } =
        useFilterContext();

    const filterSectionList = displayedFilters.filter_section_list;

    if (activeFilterId === FILTER_SECTION.moreFilter) {
        const items = filterSectionList.filter((section) => {
            // exclude disabled Sale filter
            if (
                section.id === FILTER_SECTION.saleFilter &&
                section.option_groups[0].options.length === 0
            ) {
                return false;
            }

            return !!section.render_condition; // condition is not falsey i.e. is "main" or "more";
        });

        return <MoreFilters items={items} {...props} />;
    }

    const selectedSectionData = filterSectionList.filter(
        (section) => section.id === activeFilterId
    )[0];

    const selectedFilterData = displayedFilters.filter_section_list.find(
        (section) => section.id === selectedSectionData.id
    );
    const categoryFilterData = displayedFilters.filter_section_list.find(
        (section) => section.id === FILTER_SECTION.categoryFilter
    );
    const isAnyCategorySelected = isAnyCategoryOptionChecked(categoryFilterData);
    const genderSelection = displayedFilters.filter_section_list.find(
        (section) => section.id === FILTER_SECTION.genderFilter
    );

    if (!selectedSectionData || !selectedFilterData) {
        return null;
    }

    const specificSelectedFilter = selectedFilterInfo[selectedSectionData.id];

    const optionGroup = selectedFilterData.option_groups[0];

    if (selectedSectionData.id.startsWith(FILTER_SECTION.extraAttributeFilter)) {
        return (
            <React.Suspense fallback={!props.lazyFacetIsLoading && <SpinnerEle />}>
                <AttributeFilters options={(optionGroup?.options as InputFilterOption[]) || []} />
            </React.Suspense>
        );
    }

    switch (selectedSectionData.id) {
        case FILTER_SECTION.retailerFilter:
        case FILTER_SECTION.materialFilter:
        case FILTER_SECTION.brandFilter:
            return (
                <React.Suspense fallback={!props.lazyFacetIsLoading && <SpinnerEle />}>
                    <FilterOptionGroup
                        isVisible
                        optionGroup={optionGroup as FilteredScrollAreaFilterOptionGroup}
                        lazyFacetFetch={props.lazyFacetFetch}
                        lazyFacetIsLoading={props.lazyFacetIsLoading}
                        title={selectedFilterData?.title}
                    />
                </React.Suspense>
            );
        case FILTER_SECTION.categoryFilter:
            return (
                <React.Suspense fallback={!props.lazyFacetIsLoading && <SpinnerEle />}>
                    <Categories
                        options={optionGroup?.options as CategoryFilterOption[]}
                        genderFilterInfo={genderSelection || ({} as UniversalFilterSection)}
                    />
                </React.Suspense>
            );
        case FILTER_SECTION.priceFilter:
            return (
                <React.Suspense fallback={!props.lazyFacetIsLoading && <SpinnerEle />}>
                    <PriceFilter
                        item={selectedFilterData as PairOfSelectsFilterSection}
                        sectionData={selectedSectionData}
                        selectedFilterInfo={specificSelectedFilter}
                        {...props}
                    />
                </React.Suspense>
            );
        case FILTER_SECTION.colorFilter:
            return (
                <React.Suspense fallback={!props.lazyFacetIsLoading && <SpinnerEle />}>
                    <FeedFilterColors
                        item={selectedFilterData as ColorsFilterSection}
                        onClick={onFilterItemClick}
                        pendingFilterInfo={pendingFilterInfo}
                        {...props}
                    />
                </React.Suspense>
            );
        case FILTER_SECTION.sizeFilter:
            return (
                <React.Suspense fallback={!props.lazyFacetIsLoading && <SpinnerEle />}>
                    <FeedFilterSizes
                        item={selectedFilterData as SizesFilterSection}
                        onClick={onFilterItemClick}
                        pendingFilterInfo={pendingFilterInfo}
                        genderFilterInfo={genderSelection}
                        isAnyCategorySelected={isAnyCategorySelected}
                        {...props}
                    />
                </React.Suspense>
            );
        case FILTER_SECTION.taxonomyDressStyleFilter:
        case FILTER_SECTION.taxonomyDressLengthFilter:
        case FILTER_SECTION.taxonomyDressNecklineFilter:
        case FILTER_SECTION.taxonomyDressOccasionFilter:
        case FILTER_SECTION.taxonomyDressSleeveFilter:
            return (
                <React.Suspense fallback={!props.lazyFacetIsLoading && <SpinnerEle />}>
                    <FeedFilterGeneric
                        item={selectedFilterData as NormalFilterSection}
                        pendingFilterInfo={pendingFilterInfo}
                        {...props}
                    />
                </React.Suspense>
            );
        case FILTER_SECTION.genderFilter:
        case FILTER_SECTION.shippingFilter:
        case FILTER_SECTION.saleFilter:
            return (
                <React.Suspense fallback={!props.lazyFacetIsLoading && <SpinnerEle />}>
                    <CheckboxFilters
                        items={[selectedFilterData] as UniversalFilterSection[]}
                        onClick={onFilterItemClick}
                        pendingFilterInfo={pendingFilterInfo}
                        {...props}
                    />
                </React.Suspense>
            );
        default:
            return null;
    }
}
